import { AssetEntity, GeofenceEntityType, MicrofenceEntity } from '../enums';
import {
  AssetBattery,
  AssetCoordinates,
  AssetSensed,
  AssetSensedTriggered,
  AssetWelfareCheck,
  AssetCoordinatesDwell,
  AssetCoordinatesTriggered,
  AssetSensorTriggeredBoundary,
  AssetSpo2,
  AssetTemperature,
} from './schema';
import { Microfence } from '../../API/types/triggers';
import { POINT } from '../constants';
import { ExtendedMap } from '../mapUtils';
import { PortableAssetDto } from '../../API/types/metadata';

export interface Notification {
  id: string;
  date: Date;
  seen: boolean;
  replayed?: boolean;
  asset: AssetState;
  updateType: AssetUpdateType.SensedTriggered | AssetUpdateType.CoordinatesTriggered;
}

export interface AssetState {
  id: Record<string, string | undefined>;
  label?: string;
  type?: AssetEntity;
  lastUpdate?: Date;
  lastStreamed?: { when: Date; event?: AssetUpdateType };
  lastKnownIso8601?: string;
  lastBattery?: AssetBattery; // The very last battery reading
  lastSensed?: AssetSensed; // The very last asset sensed
  recentSensed?: AssetSensed[]; // Recent history of loctions assets sensed
  recentSensedCount?: number;
  lastSensedBy?: IdentifiersAndLabel;
  lastSensedTriggered?: AssetSensedTriggered; // The very last asset sensed triggered (eg an asset entering a microfence)
  recentSensedTriggered?: AssetSensedTriggered[]; // Recent history of loctions assets sensed triggered
  recentLocations?: AssetCoordinates[]; // Recent history of loctions
  lastFenceEventDwell?: AssetCoordinatesTriggered | AssetCoordinatesDwell; // The very last fence event
  lastFenceEventBackend?: AssetCoordinatesTriggered | AssetCoordinatesDwell; // The very last fence event
  lastFenceEventMobile?: AssetCoordinatesTriggered | AssetCoordinatesDwell; // The very last fence event
  recentFenceEvents?: (AssetCoordinatesTriggered | AssetCoordinatesDwell)[]; // Recent history of fence events
  lastSensedTriggeredBoundary?: AssetSensorTriggeredBoundary; // Last triggered event for Temperature, Spo2, etc changed
  temperature?: AssetTemperature; // Last Temperature, Spo2, etc changed
  lastWelfareCheck?: AssetWelfareCheck;
  microfence?: Microfence;
  tool?: PortableAssetDto;
  selected?: boolean;
  following?: boolean;
}

export interface IdentifiersAndLabel {
  id: Record<string, string | undefined>;
  label: string;
}

export interface SimpleGeofence {
  id: string;
  name: string;
  type: typeof POINT;
  fenceType: GeofenceEntityType | MicrofenceEntity;
  layerId?: string;
  layerName?: string;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
  errorRadiusMetres?: number;
  trueAltitude?: number;
}

export interface GeofencePoint {
  geofence: SimpleGeofence;
  point: { type: string; coordinates: string[] };
}

export interface LiveMapState {
  assets: ExtendedMap<string, AssetState>;
  lastUpdate: Date;
  lastStreamed?: { when: Date; event?: AssetUpdateType };
  startDate: Date;
  notifications: Notification[];
}

export enum AssetUpdateType {
  FullStateUpdate = 'FULL_STATE_UPDATE',
  FullStateMerge = 'FULL_STATE_MERGE',
  Battery = 'battery',
  Coordinates = 'coordinates',
  CoordinatesDwell = 'coordinates.dwell',
  CoordinatesTriggered = 'coordinates.triggered',
  CoordinatesTriggeredExternal = 'coordinates.triggered.external',
  Sensed = 'sensed',
  SensedTriggered = 'sensed.triggered',
  SensorTriggeredBoundary = 'sensed.triggered.boundary',
  Spo2 = 'spo2',
  Temperature = 'temperature',
  WelfareCheck = 'asset.welfare.check',
}

export type LiveStateUpdate =
  | {
      type: AssetUpdateType.Battery;
      data: AssetBattery;
    }
  | {
      type: AssetUpdateType.Coordinates;
      data: AssetCoordinates;
    }
  | {
      type: AssetUpdateType.CoordinatesDwell;
      data: AssetCoordinatesDwell;
    }
  | {
      type: AssetUpdateType.CoordinatesTriggered;
      data: AssetCoordinatesTriggered;
    }
  | {
      type: AssetUpdateType.CoordinatesTriggeredExternal;
      data: AssetCoordinatesTriggered;
    }
  | {
      type: AssetUpdateType.Sensed;
      data: AssetSensed;
    }
  | {
      type: AssetUpdateType.SensedTriggered;
      data: AssetSensedTriggered;
    }
  | {
      type: AssetUpdateType.SensorTriggeredBoundary;
      data: AssetSensorTriggeredBoundary;
    }
  | {
      type: AssetUpdateType.Spo2;
      data: AssetSpo2;
    }
  | {
      type: AssetUpdateType.Temperature;
      data: AssetTemperature;
    }
  | {
      type: AssetUpdateType.WelfareCheck;
      data: AssetWelfareCheck;
    };

export type LiveStateOrFullUpdate =
  | {
      type: AssetUpdateType.FullStateUpdate;
      data: LiveMapState;
    }
  | {
      type: AssetUpdateType.FullStateMerge;
      data: LiveMapState;
    }
  | LiveStateUpdate;
